/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import BenefitsModule from '@/components/modules/benefit-module/benefit-module';
import OnboardingFinishStepModule from '@/components/modules/onboarding-flow-finish-step-form-module/onboarding-flow-finish-step-form-module';
import OnboardingMembershipStepModule from '@/components/modules/onboarding-flow-membership-step-form-module/onboarding-flow-membership-step-form-module';
import OnboardingNameStepModule, {
  type OnboardingNameStepData,
} from '@/components/modules/onboarding-flow-name-step-form-module/onboarding-flow-name-step-form-module';
import OnboardingFlowStepHeaderModule from '@/components/modules/onboarding-flow-step-header-module/onboarding-flow-step-header-module';
import OnboardingFlowStepHeaderModuleContainer from '@/components/modules/onboarding-flow-step-header-module/onboarding-flow-step-header-module.container';

export type OnboardingFlowModule =
  Content.Components['OnboardingFlowStepDto']['contentModules'][number];

export interface OnboardingFlowModuleListProps {
  /** Whether the membership step is loading */
  isSubmitting?: boolean;
  /** The modules to render */
  modules: OnboardingFlowModule[];
  /** Callback fired when the membership step is completed */
  onMembershipStepComplete?: (
    acceptedMembership: boolean,
  ) => Promise<void> | void;
  /** Callback fired when the name step is completed */
  onNameStepComplete?: (values: OnboardingNameStepData) => void;
}

type OnboardingFlowMappedModule =
  | ({ $type: 'BenefitModuleDto' } & Content.Components['BenefitModuleDto'])
  | ({
      $type: 'OnboardingFlowFinishStepFormModuleDto';
    } & Content.Components['OnboardingFlowFinishStepFormModuleDto'])
  | ({
      $type: 'OnboardingFlowMembershipStepFormModuleDto';
    } & Content.Components['OnboardingFlowMembershipStepFormModuleDto'])
  | ({
      $type: 'OnboardingFlowNameStepFormModuleDto';
    } & Content.Components['OnboardingFlowNameStepFormModuleDto'])
  | ({
      $type: 'OnboardingFlowStepHeaderModuleDto';
    } & Content.Components['OnboardingFlowStepHeaderModuleDto'])
  | ({
      $type: 'OnboardingFlowStepHeaderHeadlessModuleDto';
    } & Content.Components['OnboardingFlowStepHeaderHeadlessModuleDto']);

const OnboardingFlowModuleMap: {
  [key in OnboardingFlowMappedModule['$type']]: React.ComponentType<any>;
} = {
  BenefitModuleDto: BenefitsModule,
  OnboardingFlowFinishStepFormModuleDto: OnboardingFinishStepModule,
  OnboardingFlowMembershipStepFormModuleDto: OnboardingMembershipStepModule,
  OnboardingFlowNameStepFormModuleDto: OnboardingNameStepModule,
  OnboardingFlowStepHeaderHeadlessModuleDto:
    OnboardingFlowStepHeaderModuleContainer,
  OnboardingFlowStepHeaderModuleDto: OnboardingFlowStepHeaderModule,
};

function getOnboardingFlowModule(
  type: OnboardingFlowMappedModule['$type'],
  props: Omit<OnboardingFlowMappedModule, '$type'>,
): {
  component: React.ComponentType<any>;
  props: Omit<OnboardingFlowMappedModule, '$type'>;
} {
  return { component: OnboardingFlowModuleMap[type], props };
}

const OnboardingFlowModuleList = ({
  isSubmitting = false,
  modules = [],
  onMembershipStepComplete,
  onNameStepComplete,
}: OnboardingFlowModuleListProps) => {
  return modules.map((module, moduleIndex) => {
    const typedModule = module as OnboardingFlowMappedModule;
    const { component: Component, props } = getOnboardingFlowModule(
      typedModule.$type,
      typedModule,
    );

    if (!Component) return null;

    if (typedModule.$type === 'OnboardingFlowNameStepFormModuleDto') {
      return (
        <Component
          key={moduleIndex}
          {...props}
          onComplete={(values: OnboardingNameStepData) => {
            onNameStepComplete?.(values);
          }}
        />
      );
    }

    if (typedModule.$type === 'OnboardingFlowMembershipStepFormModuleDto') {
      return (
        <Component
          key={moduleIndex}
          {...props}
          isLoading={isSubmitting}
          onComplete={async (acceptedMembership: boolean) => {
            onMembershipStepComplete?.(acceptedMembership);
          }}
        />
      );
    }

    if (typedModule.$type === 'OnboardingFlowFinishStepFormModuleDto') {
      return <Component key={moduleIndex} {...props} />;
    }

    return <Component key={moduleIndex} {...props} />;
  });
};

OnboardingFlowModuleList.displayName = 'OnboardingFlowModuleList';

export default OnboardingFlowModuleList;
